import React, { ChangeEvent, useState, FC } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import classNames from 'classnames'
import TextInputGroup from 'components/atoms/TextInputGroup'
import moment from 'moment-timezone'
import PatternFormat from 'transfix-ui/components/PatternFormat'
import { Box } from 'transfix-ui/core'
import { TextInput } from 'components/atoms'
import { FORMATS } from 'constants/datetime'

export type StopType = 'pickupsAttributes' | 'deliveriesAttributes'

export interface IStopFieldProps {
  index: number
  type: StopType
  name: string
}

export interface IDateTimeFieldsProps {
  label: string
  timezone?: string
  value?: string | null
  isMissing?: boolean
  isDisabled?: boolean
  autofillDate?: string
  onChange?: (val: string) => void
  stopFieldProps: IStopFieldProps
}

/**
 * DateTime inputs using native 'date' input and masked 'time' input.
 * Note: This is only used in the current iteration (MVP) of the feature & will likely
 *  be removed/changed following user feedback.
 * @deprecated
 */
const DateTimeFields: FC<IDateTimeFieldsProps> = ({
  timezone = FORMATS.defaultTimeZone,
  isMissing = false,
  isDisabled,
  autofillDate,
  label,
  stopFieldProps,
  onChange,
}) => {
  const { index: stopIdx, name, type: stopTypeKey } = stopFieldProps || {}
  const inputId = `${stopTypeKey}[${stopIdx}].${name}`
  const value = useWatch({ name: inputId })
  const { formState } = useFormContext()
  const displayDate = value || autofillDate
  const [dateTimeVal, setDateTimeVal] = useState({
    date: displayDate ? moment.tz(displayDate, timezone).format(FORMATS.yearFirst) : '',
    time:
      value && !autofillDate // Don't autofill time input
        ? moment.tz(value, timezone).format(FORMATS.militaryTimeFormat)
        : '',
  })

  const changeHandler = ({
    target: { name: fieldName, value: fieldValue },
  }: ChangeEvent<HTMLInputElement>) => {
    const dateTimeInput = {
      ...dateTimeVal,
      [fieldName]: fieldValue,
    }

    setDateTimeVal(dateTimeInput)

    const timeFormatCheck = new RegExp(/\d{2}:\d{2}/) // HH:mm format
    const hasTimeEntry = timeFormatCheck.test(dateTimeInput.time)

    // Convert entry in local time to stop timezone
    const dateISO = hasTimeEntry
      ? moment
          .tz(`${dateTimeInput.date} ${dateTimeInput.time}`, timezone)
          .utc()
          .format(FORMATS.iso8601Format)
      : 'Invalid time'
    if (onChange) {
      onChange(dateISO)
    }
  }

  const errors = formState.errors[stopTypeKey] || []
  const errorFields = errors[stopIdx] ? errors[stopIdx][name] : undefined

  const minDate = moment().subtract(3, 'months').format(FORMATS.yearFirst)
  const maxDate = moment().add(3, 'days').format(FORMATS.yearFirst)

  // Pick up `TextInput` styles
  const timeInputClasses = classNames('text-input', {
    disabled: isDisabled,
    'text-input-highlighted-warning': isMissing,
    invalid: errorFields,
  })

  return (
    <Box display='flex' flexDirection='column'>
      <TextInputGroup label={label} mr={2} width={180}>
        <TextInput
          dataTest={`${inputId}-date`}
          value={dateTimeVal.date}
          highlighted={isMissing ? 'warning' : undefined}
          type='date'
          name='date'
          onChange={changeHandler}
          disabled={isDisabled}
          clearable={false}
          min={minDate}
          max={maxDate}
          invalid={errorFields}
        />
        <Box className={timeInputClasses} maxWidth={70}>
          <Box className='text-input-container'>
            <PatternFormat
              data-test={`${inputId}-time`}
              value={dateTimeVal.time}
              type='text'
              name='time'
              mask='_'
              format='##:##'
              placeholder='HH:mm'
              onChange={changeHandler}
              disabled={isDisabled}
              displayType='input'
            />
          </Box>
        </Box>
      </TextInputGroup>
      {errorFields && <div className='text-error validation-message'>{errorFields?.message}</div>}
    </Box>
  )
}

export default DateTimeFields
